import React, { useEffect, useState } from 'react';
import {
  CalendarDays,
  Clock,
  Briefcase,
  Bell,
  AlertTriangle,
  Download,
  Smartphone,
  Phone,
  ChevronRight,
} from 'lucide-react';
import Joyride, { Step } from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { GLASS_CARD } from '@/components/ui/glass';
import { getDashboardWorkerTourSteps } from '@/components/tour/worker/DashboardWorkerTourSteps';
import { getTourStyles } from '@/components/tour/tourStyles';
import { useDashboardWorker, ProximaCita } from '@/components/hooks/useDashboardWorker';
import { useInstallPWA } from '@/components/pwa/useInstallPWA';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import MetricCard from '@/components/pages/company/dashboard/MetricCard';
import DashboardSeccion from '@/components/pages/company/dashboard/DashboardSeccion';
import DashboardHorasPico from '@/components/pages/company/dashboard/DashboardHorasPico';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 320, damping: 30, mass: 0.7 },
  },
};
const gridVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.35, ease: 'easeOut' as const } },
};

const PWA_BANNER_DISMISSED_KEY = 'pwa_banner_dismissed';
const BASE = '/panel-trabajador';

const shimmer =
  'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/40 dark:before:via-white/10 before:to-transparent';
const bone = 'rounded-md bg-gray-200/70 dark:bg-gray-700/50';

const MetricsSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} p-4 space-y-3`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-xl ${bone} ${shimmer}`} />
          <div className={`h-3 w-20 ${bone} ${shimmer}`} />
        </div>
        <div className={`h-7 w-12 ${bone} ${shimmer}`} />
      </div>
    ))}
  </div>
);

const ListaSkeleton: React.FC = () => (
  <div className={`rounded-2xl ${GLASS_CARD} divide-y divide-gray-100 dark:divide-white/5`}>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-4">
        <div className={`w-12 h-12 rounded-xl ${bone} ${shimmer}`} />
        <div className="flex-1 space-y-2">
          <div className={`h-3.5 w-32 ${bone} ${shimmer}`} />
          <div className={`h-3 w-20 ${bone} ${shimmer}`} />
        </div>
      </div>
    ))}
  </div>
);

const ESTADOS: Record<string, string> = {
  confirmado:
    'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  pendiente:
    'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  completado:
    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  cancelado:
    'bg-red-500/10 text-red-600 dark:text-red-400',
};

const aFecha = (iso: string) => new Date(`${String(iso).slice(0, 10)}T00:00:00`);

const etiquetaDia = (iso: string) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const f = aFecha(iso);
  const dias = Math.round((f.getTime() - hoy.getTime()) / 86400000);
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Mañana';
  const txt = f.toLocaleDateString('es-ES', { weekday: 'long' });
  return txt.charAt(0).toUpperCase() + txt.slice(1);
};

const FilaCita = ({ cita }: { cita: ProximaCita }) => {
  const f = aFecha(cita.fecha_cita);
  const esHoy = etiquetaDia(cita.fecha_cita) === 'Hoy';
  const estado = cita.estado_cita?.toLowerCase() ?? '';
  return (
    <div className="flex items-center gap-3 p-3.5 sm:p-4">
      <div
        className={`shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center leading-none ${
          esHoy
            ? 'bg-[#2170b8] text-white'
            : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200'
        }`}
      >
        <span className="text-lg font-bold tabular-nums">{f.getDate()}</span>
        <span className={`text-[10px] uppercase ${esHoy ? 'text-white/80' : 'text-gray-400'}`}>
          {f.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '')}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {cita.nombre_servicio}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {etiquetaDia(cita.fecha_cita)}
          </span>
          <span className="tabular-nums">
            {cita.hora_cita?.slice(0, 5)}
            {cita.hora_fin ? ` – ${cita.hora_fin.slice(0, 5)}` : ''}
          </span>
          {cita.telefono && (
            <a
              href={`tel:${cita.telefono}`}
              className="inline-flex items-center gap-1 tabular-nums hover:text-[#2170b8]"
            >
              <Phone className="w-3 h-3" />
              {cita.telefono}
            </a>
          )}
        </div>
      </div>

      <span
        className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
          ESTADOS[estado] ?? 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300'
        }`}
      >
        {estado}
      </span>
    </div>
  );
};

const DashboardWorker: React.FC = () => {
  const navigate = useNavigate();
  const { loading, stats } = useDashboardWorker();
  const [runTour, setRunTour] = useState(false);
  const [preparandoTour, setPreparandoTour] = useState(false);
  const [tourSteps, setTourSteps] = useState<Step[]>([]);
  const [cargadoUnaVez, setCargadoUnaVez] = useState(false);
  const isMobile = window.innerWidth < 640;

  const empresaId = localStorage.getItem('id_empresa') || '';
  const userId = localStorage.getItem('id_usuario') || '';
  const hoy = new Date();

  const mostrarSkeleton = loading && !cargadoUnaVez;
  const seccionesAbiertas = preparandoTour || runTour;

  useEffect(() => {
    if (!loading) setCargadoUnaVez(true);
  }, [loading]);

  useEffect(() => {
    if (!preparandoTour) return;
    const id = window.setTimeout(() => {
      setTourSteps(getDashboardWorkerTourSteps());
      setRunTour(true);
      setPreparandoTour(false);
    }, 350);
    return () => window.clearTimeout(id);
  }, [preparandoTour]);

  usePageHelp(() => setPreparandoTour(true), !mostrarSkeleton);
  useTourVisibility(runTour);

  const { canShow, isIOS, install } = useInstallPWA();
  const pwaBannerDismissed =
    localStorage.getItem(PWA_BANNER_DISMISSED_KEY) === 'true';
  const showPwaButton = canShow && pwaBannerDismissed;

  const metricas = [
    {
      titulo: 'Citas para hoy',
      valor: stats.citasHoy,
      icono: <CalendarDays />,
      tono: 'blue' as const,
      onClick: () => navigate(`${BASE}/workerCalendar`),
      linkLabel: 'Ir a mi calendario',
      className: 'tour-metric-citasHoy',
    },
    {
      titulo: 'Citas pendientes',
      valor: stats.citasPendientes,
      icono: <Clock />,
      tono: 'amber' as const,
      onClick: () => navigate(`${BASE}/workerAppointments`),
      linkLabel: 'Ir a mis citas',
      className: 'tour-metric-citasPendientes',
    },
    {
      titulo: 'Mis servicios',
      valor: stats.servicios,
      icono: <Briefcase />,
      tono: 'slate' as const,
      className: 'tour-metric-servicios',
    },
    {
      titulo: 'Notificaciones',
      valor: stats.notificaciones,
      icono: <Bell />,
      tono: 'violet' as const,
      className: 'tour-metric-notificaciones',
    },
  ];

  const proximas = stats.proximasCitas.slice(0, 5);

  return (
    <motion.div
      className="space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="pr-32">
        <div className="tour-title">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Inicio
          </h1>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            {(() => {
              const s = hoy.toLocaleDateString('es-ES', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              });
              return s.charAt(0).toUpperCase() + s.slice(1);
            })()}
          </p>
        </div>
      </motion.div>

      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        disableOverlayClose={false}
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {!mostrarSkeleton && stats.noLaborableHoy && (
        <motion.div
          variants={itemVariants}
          className="tour-alert relative isolate overflow-hidden flex items-center gap-3 p-4 bg-yellow-50/90 dark:bg-yellow-500/10 border border-yellow-200/80 dark:border-yellow-500/30 rounded-2xl"
        >
          <div className="w-9 h-9 shrink-0 rounded-xl bg-yellow-400/20 dark:bg-yellow-400/15 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-yellow-700 dark:text-yellow-300">
              Día no laborable
            </h2>
            <p className="text-xs text-yellow-600 dark:text-yellow-200/80 mt-0.5">
              Hoy no tienes horario asignado en tu agenda.
            </p>
          </div>
        </motion.div>
      )}

      {showPwaButton && (
        <motion.div
          variants={itemVariants}
          className="tour-pwa-banner p-4 rounded-2xl bg-yellow-50/90 text-blue-800 border border-yellow-200/80 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-700/40"
        >
          {isIOS ? (
            <p className="text-xs text-blue-700 dark:text-amber-300 flex items-center gap-1.5">
              <Smartphone className="w-3.5 h-3.5 shrink-0" />
              Para instalar la app: pulsa compartir en Safari → "Añadir a
              pantalla de inicio"
            </p>
          ) : (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={install}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#103254] hover:bg-[#0d2742] px-3.5 py-2 rounded-xl"
            >
              <Download className="w-3.5 h-3.5" />
              Instalar app
            </motion.button>
          )}
        </motion.div>
      )}

      <DashboardSeccion id="trabajador-resumen" titulo="Tu día" forzarAbierta={seccionesAbiertas}>
        <AnimatePresence mode="wait">
          {mostrarSkeleton ? (
            <motion.div key="sk" variants={fadeVariants} initial="hidden" animate="visible" exit="hidden">
              <MetricsSkeleton />
            </motion.div>
          ) : (
            <motion.div
              key="real"
              variants={gridVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
            >
              {metricas.map((m) => (
                <MetricCard key={m.className} {...m} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </DashboardSeccion>

      <DashboardSeccion
        id="trabajador-proximas"
        titulo="Próximas citas"
        forzarAbierta={seccionesAbiertas}
        resumen={proximas.length ? `${stats.citasPendientes} pendientes` : undefined}
      >
        {mostrarSkeleton ? (
          <ListaSkeleton />
        ) : (
          <div className={`tour-upcoming relative isolate overflow-hidden rounded-2xl ${GLASS_CARD}`}>
            {proximas.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No tienes citas próximas.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-white/5">
                {proximas.map((c) => (
                  <FilaCita key={c.id_cita} cita={c} />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate(`${BASE}/workerAppointments`)}
              className="tour-upcoming-all w-full flex items-center justify-center gap-1 border-t border-gray-100 dark:border-white/5 px-4 py-3 text-xs font-semibold text-[#2170b8] dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              Ver todas mis citas
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </DashboardSeccion>

      <DashboardSeccion id="trabajador-horas" titulo="Tus horas pico" forzarAbierta={seccionesAbiertas}>
        <DashboardHorasPico
          empresaId={empresaId}
          usuarioId={userId}
          mes={hoy.getMonth() + 1}
          anio={hoy.getFullYear()}
        />
      </DashboardSeccion>
    </motion.div>
  );
};

export default DashboardWorker;
